import { PositionAnalysis } from '@/types/portfolio';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { TrendingUp, TrendingDown, AlertTriangle, Target, ShieldAlert, DollarSign } from 'lucide-react';

interface StockScorecardProps {
  analyses: PositionAnalysis[];
}

export function StockScorecard({ analyses }: StockScorecardProps) {
  if (analyses.length === 0) return null;

  const getCurrencySymbol = (currency?: string): string => {
    if (!currency) return '$';
    switch (currency.toUpperCase()) {
      case 'USD': return '$';
      case 'EUR': return '€';
      case 'CHF': return 'CHF';
      case 'GBP': return '£';
      case 'CAD': return 'CA$';
      case 'JPY': return '¥';
      default: return currency;
    }
  };

  // Filter positions with alerts
  const alertPositions = analyses.filter(a => 
    a.alertLevel && (a.alertLevel.type === 'profit' || a.alertLevel.type === 'warning' || a.alertLevel.type === 'danger')
  );

  // Separate by action type
  const takeProfitPositions = alertPositions.filter(a => a.alertLevel?.type === 'danger' && a.gainPct > 0);
  const profitZonePositions = alertPositions.filter(a => a.alertLevel?.type === 'warning');
  const profitAlertPositions = alertPositions.filter(a => a.alertLevel?.type === 'profit');
  const stopLossPositions = analyses.filter(a => a.gainPct < -0.05); // Below -5%

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-foreground flex items-center gap-2">
        <Target className="h-6 w-6 text-primary" />
        Action Scorecards - Quick Decision Guide
      </h2>

      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-4 gap-4">
        {/* Take Profit Now */}
        {takeProfitPositions.length > 0 && (
          <Card className="border-2 border-red-500 bg-red-500/5">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <div className="h-3 w-3 bg-red-500 rounded-full animate-pulse"></div>
                Take Profit Now ({takeProfitPositions.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {takeProfitPositions.map((pos) => {
                const currencySymbol = getCurrencySymbol(pos.localCurrency);
                const gainPct = (pos.gainPct * 100).toFixed(2);
                return (
                  <div key={pos.id} className="p-3 bg-background rounded-lg border border-red-200">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-bold text-foreground">{pos.ticker}</span>
                      <Badge className="bg-red-500 text-white">+{gainPct}%</Badge>
                    </div>
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Sell:</span>
                        <span className="font-semibold text-foreground">
                          {pos.recommendedSellQty} shares ({pos.recommendedSellPct}%)
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Lock in:</span>
                        <span className="font-semibold text-green-600">
                          {currencySymbol}{(pos.recommendedSellQty * pos.currentPrice).toLocaleString()}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Stop Loss:</span>
                        <span className="font-semibold text-red-600">
                          {currencySymbol}{pos.stopLoss?.[0]?.price.toFixed(2)}
                        </span>
                      </div>
                    </div>
                    <div className="mt-2 pt-2 border-t border-border">
                      <p className="text-xs font-medium text-red-600">
                        🎯 Action: {pos.actionRecommendation}
                      </p>
                    </div>
                  </div>
                );
              })}
            </CardContent>
          </Card>
        )}

        {/* Profit Zone - Consider Taking Profits */}
        {profitZonePositions.length > 0 && (
          <Card className="border-2 border-orange-500 bg-orange-500/5">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-orange-500" />
                Profit Zone ({profitZonePositions.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {profitZonePositions.map((pos) => {
                const currencySymbol = getCurrencySymbol(pos.localCurrency);
                const gainPct = (pos.gainPct * 100).toFixed(2);
                const nextTarget = pos.profitTargets?.find(t => pos.currentPrice < t.targetPrice);
                return (
                  <div key={pos.id} className="p-3 bg-background rounded-lg border border-orange-200">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-bold text-foreground">{pos.ticker}</span>
                      <Badge className="bg-orange-500 text-white">+{gainPct}%</Badge>
                    </div>
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Current:</span>
                        <span className="font-semibold text-foreground">
                          {currencySymbol}{pos.currentPrice.toFixed(2)}
                        </span>
                      </div>
                      {nextTarget && (
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Next Target:</span>
                          <span className="font-semibold text-green-600">
                            {currencySymbol}{nextTarget.targetPrice.toFixed(2)} ({nextTarget.ratio})
                          </span>
                        </div>
                      )}
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Stop Loss:</span>
                        <span className="font-semibold text-red-600">
                          {currencySymbol}{pos.stopLoss?.[0]?.price.toFixed(2)}
                        </span>
                      </div>
                    </div>
                    <div className="mt-2 pt-2 border-t border-border">
                      <p className="text-xs font-medium text-orange-600">
                        ⚡ Action: Partial profit-taking recommended
                      </p>
                    </div>
                  </div>
                );
              })}
            </CardContent>
          </Card>
        )}

        {/* Profit Alert - Monitor Closely */}
        {profitAlertPositions.length > 0 && (
          <Card className="border-2 border-yellow-500 bg-yellow-500/5">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-yellow-600" />
                Profit Alert ({profitAlertPositions.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {profitAlertPositions.map((pos) => {
                const currencySymbol = getCurrencySymbol(pos.localCurrency);
                const gainPct = (pos.gainPct * 100).toFixed(2);
                const nextTarget = pos.profitTargets?.find(t => pos.currentPrice < t.targetPrice);
                return (
                  <div key={pos.id} className="p-3 bg-background rounded-lg border border-yellow-200">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-bold text-foreground">{pos.ticker}</span>
                      <Badge className="bg-yellow-500 text-white">+{gainPct}%</Badge>
                    </div>
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Risk:Reward:</span>
                        <span className="font-semibold text-foreground">
                          {pos.riskRewardRatio}
                        </span>
                      </div>
                      {nextTarget && (
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Target:</span>
                          <span className="font-semibold text-green-600">
                            {currencySymbol}{nextTarget.targetPrice.toFixed(2)}
                          </span>
                        </div>
                      )}
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Stop Loss:</span>
                        <span className="font-semibold text-red-600">
                          {currencySymbol}{pos.stopLoss?.[0]?.price.toFixed(2)}
                        </span>
                      </div>
                    </div>
                    <div className="mt-2 pt-2 border-t border-border">
                      <p className="text-xs font-medium text-yellow-700">
                        💰 Action: Monitor for selling opportunity
                      </p>
                    </div>
                  </div>
                );
              })}
            </CardContent>
          </Card>
        )}

        {/* Stop Loss Warning */}
        {stopLossPositions.length > 0 && (
          <Card className="border-2 border-red-600 bg-red-600/5">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <ShieldAlert className="h-5 w-5 text-red-600" />
                Stop Loss Alert ({stopLossPositions.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {stopLossPositions.map((pos) => {
                const currencySymbol = getCurrencySymbol(pos.localCurrency);
                const lossPct = (pos.gainPct * 100).toFixed(2);
                return (
                  <div key={pos.id} className="p-3 bg-background rounded-lg border border-red-300">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-bold text-foreground">{pos.ticker}</span>
                      <Badge variant="destructive">{lossPct}%</Badge>
                    </div>
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Loss:</span>
                        <span className="font-semibold text-red-600">
                          {currencySymbol}{Math.abs(pos.gainAmount).toLocaleString()}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Entry:</span>
                        <span className="font-semibold text-muted-foreground">
                          {currencySymbol}{pos.buyPrice.toFixed(2)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Current:</span>
                        <span className="font-semibold text-foreground">
                          {currencySymbol}{pos.currentPrice.toFixed(2)}
                        </span>
                      </div>
                    </div>
                    <div className="mt-2 pt-2 border-t border-border">
                      <p className="text-xs font-medium text-red-600">
                        🛑 Action: Consider cutting losses
                      </p>
                    </div>
                  </div>
                );
              })}
            </CardContent>
          </Card>
        )}
      </div>

      {/* Summary Stats */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <DollarSign className="h-5 w-5" />
            Portfolio Alert Summary
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center p-4 bg-red-500/10 rounded-lg">
              <div className="text-3xl font-bold text-red-600">{takeProfitPositions.length}</div>
              <div className="text-sm text-muted-foreground mt-1">Take Profit Now</div>
            </div>
            <div className="text-center p-4 bg-orange-500/10 rounded-lg">
              <div className="text-3xl font-bold text-orange-600">{profitZonePositions.length}</div>
              <div className="text-sm text-muted-foreground mt-1">In Profit Zone</div>
            </div>
            <div className="text-center p-4 bg-yellow-500/10 rounded-lg">
              <div className="text-3xl font-bold text-yellow-600">{profitAlertPositions.length}</div>
              <div className="text-sm text-muted-foreground mt-1">Profit Alerts</div>
            </div>
            <div className="text-center p-4 bg-red-600/10 rounded-lg">
              <div className="text-3xl font-bold text-red-700">{stopLossPositions.length}</div>
              <div className="text-sm text-muted-foreground mt-1">Stop Loss Alerts</div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
